import {
    type MediaFormat,
    sourceHasValidMedia,
} from "~components/shared/media";
import type { CuratedItem, ZoneColumnCombo } from "~types/types";

export const INTEGRITY_FAIL_MEDIA = "Missing or Invalid type of media";

export type IntegrityStatus = string[];

export function firstItemOnly<T>(items?: T[]) {
    return (items?.slice(0, 1) as T[]) || [];
}

export const truthyFilter = <T>(
    x: T | false | undefined | null | "" | 0
): x is T => Boolean(x);

export function checkItemIntegrity<T extends CuratedItem>(
    item: T,
    properties?: (keyof T)[]
): IntegrityStatus {
    if (!item) return ["Missing item"];

    if (!properties?.length) return [];

    const requiredProperties: (keyof T)[] = [...properties];

    const messages: string[] = requiredProperties
        .map((property) => {
            if (!item[property]) {
                return `Missing ${property.toString()}`;
            }
            return null;
        })
        .filter(truthyFilter);

    return messages;
}

export function checkItemArrayIntegrity<T extends CuratedItem>(
    items?: T[],
    properties?: (keyof T)[]
): IntegrityStatus {
    const messages: IntegrityStatus = [];

    if (!items?.length) return ["Empty items array"];

    if (!properties?.length) return [];

    // labelAllEntries - do we want to return early here?
    for (let i = 0; i < items.length; i += 1) {
        const status = checkItemIntegrity<T>(items[i], properties);

        if (status.length) {
            messages.push(
                `${status.join(", ")} on item ${i + 1} with id ${
                    items[i].id || "unknown"
                }`
            );
        }
    }

    return messages;
}

export function checkMediaIntegrity(
    items: CuratedItem[],
    priority?: MediaFormat[]
): IntegrityStatus {
    const messages: IntegrityStatus = [];

    if (!items?.length) {
        messages.push("Empty items array");
        return messages;
    }

    for (let i = 0; i < items.length; i += 1) {
        const isRenderable = sourceHasValidMedia(items[i], priority);

        if (!isRenderable) {
            messages.push(`${INTEGRITY_FAIL_MEDIA} on item ${i + 1}`);
        }
    }

    return messages;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function checkModulePropsExist(module: any, properties: string[]) {
    const messages: IntegrityStatus = [];

    properties.forEach((prop) => {
        if (!module[prop]) messages.push(`Missing '${prop}'`);
    });

    return messages;
}

export function integrityError(messages: IntegrityStatus) {
    // eslint-disable-next-line no-console
    console.error(`Integrity check failure: ${messages}`);

    return null;
}

export function checkModuleRenderingLocation(
    renderingLocation: ZoneColumnCombo | undefined,
    supportedRenderingLocations: ZoneColumnCombo[]
) {
    if (!renderingLocation) {
        return ["Rendering location is required"];
    }

    if (!supportedRenderingLocations.includes(renderingLocation)) {
        return [`"${renderingLocation}" is not a valid zone.`];
    }

    return [];
}
